import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { useNavigate } from 'react-router-dom';
import { MdEmail, MdArrowForward, MdClose } from 'react-icons/md';

interface EmailVerificationBannerProps {
  userEmail: string;
  onClose: () => void;
}

/**
 * 개인 이메일 인증 안내 배너
 * 상단에 고정되어 이메일 인증을 유도
 */
const EmailVerificationBanner: React.FC<EmailVerificationBannerProps> = ({ 
  userEmail, 
  onClose 
}) => { 
  const navigate = useNavigate();
  const [shouldShake, setShouldShake] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // 15초마다 부드럽게 강조하는 효과
  React.useEffect(() => {
    const interval = setInterval(() => {
      setShouldShake(true);
      setTimeout(() => setShouldShake(false), 800);
    }, 15000);

    return () => clearInterval(interval);
  }, []);

  const handleClick = () => {
    navigate('/settings');
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  return (
    <BannerContainer>
      <BannerContent 
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        $shouldShake={shouldShake}
        $isHovered={isHovered}
      >
        <IconWrapper $isHovered={isHovered}>
          <MdEmail size={20} />
          <PulseDot />
        </IconWrapper>
        
        <TextWrapper>
          <MainText>이메일 인증이 필요합니다</MainText>
          <SubText>
            <EmailHighlight>{userEmail}</EmailHighlight> 인증 후 모든 기능을 이용하세요
          </SubText>
        </TextWrapper>

        <ActionWrapper $isHovered={isHovered}>
          <ActionText>인증하기</ActionText>
          <ArrowIcon $isHovered={isHovered}>
            <MdArrowForward size={16} />
          </ArrowIcon>
        </ActionWrapper>

        <CloseButton onClick={handleClose} aria-label="배너 닫기">
          <MdClose size={18} />
        </CloseButton>

        <ShineEffect />
      </BannerContent>
    </BannerContainer>
  );
};

// Animations
const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
`;

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  25% { transform: translateY(-4px); }
  50% { transform: translateY(0); }
  75% { transform: translateY(-2px); }
`;

const pulse = keyframes`
  0%, 100% { 
    transform: scale(1);
    opacity: 1;
  }
  50% { 
    transform: scale(1.5);
    opacity: 0;
  }
`;

const shine = keyframes`
  0% { left: -100%; }
  50%, 100% { left: 100%; }
`;

// Styled Components
const BannerContainer = styled.div`
  position: fixed;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  animation: ${slideDown} 0.4s ease-out;
`;

const BannerContent = styled.div<{ $shouldShake: boolean; $isHovered: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px 12px 14px;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #6366f1 100%);
  border-radius: 16px;
  box-shadow: 
    0 4px 20px rgba(59, 130, 246, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s ease;
  animation: ${props => props.$shouldShake ? bounce : 'none'} 0.8s ease-in-out;
  
  ${props => props.$isHovered && `
    transform: translateY(-2px);
    box-shadow: 
      0 8px 28px rgba(59, 130, 246, 0.45),
      0 0 0 1px rgba(255, 255, 255, 0.15) inset;
  `}

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const ShineEffect = styled.div`
  position: absolute;
  top: 0;
  left: -100%;
  width: 50%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.15),
    transparent
  );
  animation: ${shine} 4s ease-in-out infinite;
  pointer-events: none;
`;

const IconWrapper = styled.div<{ $isHovered: boolean }>`
  position: relative;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: white;
  flex-shrink: 0;
  transition: all 0.3s ease;
  
  ${props => props.$isHovered && `
    background: rgba(255, 255, 255, 0.25);
    transform: scale(1.05);
  `}
`;

const PulseDot = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 8px;
  height: 8px;
  background: #fbbf24;
  border-radius: 50%;
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: #fbbf24;
    border-radius: 50%;
    animation: ${pulse} 2s ease-in-out infinite;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const MainText = styled.span`
  ${typography.subtitle.subtitle2}
  color: white;
  font-weight: 600;
  white-space: nowrap;
`;

const SubText = styled.span`
  ${typography.caption.caption1.default}
  color: rgba(255, 255, 255, 0.85);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 640px) {
    display: none;
  }
`;

const EmailHighlight = styled.span`
  color: #fbbf24;
  font-weight: 500;
`;

const ActionWrapper = styled.div<{ $isHovered: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  transition: all 0.3s ease;
  
  ${props => props.$isHovered && `
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.3);
  `}
  
  @media (max-width: 480px) {
    padding: 6px 10px;
  }
`;

const ActionText = styled.span`
  ${typography.caption.caption1.medium}
  color: white;
  white-space: nowrap;
`;

const ArrowIcon = styled.span<{ $isHovered: boolean }>`
  display: flex;
  align-items: center;
  color: white;
  transition: transform 0.3s ease;
  
  ${props => props.$isHovered && `
    transform: translateX(3px);
  `}
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-left: 4px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: white;
  }
`;

export default EmailVerificationBanner;
